/**
 * Cache management utility for performance optimization
 */

export class CacheManager {
    // Cache keys
    static CACHE_KEYS = {
        STORE_SETTINGS: 'storeSettings',
        HOME_PRODUCTS: 'homeProducts',
        USER_CART: 'userCart',
        GUEST_CART: 'guestCart',
        SALES_DATA: 'salesData'
    };

    // Cache expiration times (in milliseconds)
    static EXPIRATION = {
        STORE_SETTINGS: 5 * 60 * 1000, // 5 minutes
        HOME_PRODUCTS: 3 * 60 * 1000,  // 3 minutes
        USER_CART: 2 * 60 * 1000,      // 2 minutes
        SALES_DATA: 5 * 60 * 1000      // 5 minutes
    };

    /**
     * Get cached data if it's still valid
     * @param {string} key - Cache key
     * @param {number} maxAge - Maximum age in milliseconds
     * @returns {object|null} Cached data or null if expired/missing
     */
    static get(key, maxAge = 5 * 60 * 1000) {
        try {
            const cachedData = sessionStorage.getItem(key);
            const timestamp = sessionStorage.getItem(`${key}_timestamp`);

            if (!cachedData || !timestamp) {
                return null;
            }

            const age = Date.now() - parseInt(timestamp);
            if (age > maxAge) {
                // Remove expired cache
                this.remove(key);
                return null;
            }

            return JSON.parse(cachedData);
        } catch (error) {
            console.warn('Cache get error:', error);
            return null;
        }
    }

    /**
     * Set data in cache with timestamp
     * @param {string} key - Cache key
     * @param {any} data - Data to cache
     */
    static set(key, data) {
        try {
            sessionStorage.setItem(key, JSON.stringify(data));
            sessionStorage.setItem(`${key}_timestamp`, Date.now().toString());
        } catch (error) {
            console.warn('Cache set error:', error);
            // If storage is full, clear some old entries
            this.cleanup();
            try {
                sessionStorage.setItem(key, JSON.stringify(data));
                sessionStorage.setItem(`${key}_timestamp`, Date.now().toString());
            } catch (retryError) {
                console.warn('Cache retry failed:', retryError);
            }
        }
    }

    /**
     * Remove specific cache entry
     * @param {string} key - Cache key
     */
    static remove(key) {
        sessionStorage.removeItem(key);
        sessionStorage.removeItem(`${key}_timestamp`);
    }

    /**
     * Clear all app-related cache entries
     */
    static clearAll() {
        Object.values(this.CACHE_KEYS).forEach(key => {
            this.remove(key);
        });
    }

    /**
     * Clean up expired cache entries
     */
    static cleanup() {
        try {
            Object.entries(this.EXPIRATION).forEach(([cacheType, maxAge]) => {
                const key = this.CACHE_KEYS[cacheType];
                if (key) {
                    const timestamp = sessionStorage.getItem(`${key}_timestamp`);
                    if (timestamp) {
                        const age = Date.now() - parseInt(timestamp);
                        if (age > maxAge) {
                            this.remove(key);
                        }
                    }
                }
            });
        } catch (error) {
            console.warn('Cache cleanup error:', error);
        }
    }

    /**
     * Get cache statistics for debugging
     */
    static getStats() {
        const stats = {};
        Object.entries(this.CACHE_KEYS).forEach(([type, key]) => {
            const data = sessionStorage.getItem(key);
            const timestamp = sessionStorage.getItem(`${key}_timestamp`);

            stats[type] = {
                exists: !!data,
                size: data ? data.length : 0,
                age: timestamp ? Date.now() - parseInt(timestamp) : null
            };
        });
        return stats;
    }
}

// Auto-cleanup on page load
if (typeof window !== 'undefined') {
    CacheManager.cleanup();
}